/**
 * SlicerChipDropdown — single-dimension chip + dropdown popover.
 *
 * Renders as a chip (the parent SlicerBar lays them in a row). Click opens
 * a dropdown of checkboxes for the dim's possible values, each annotated
 * with a live trade count. Apply commits the selection; Reset clears just
 * this dim; clicking outside cancels uncommitted changes.
 *
 * Visual states:
 *   - Off (no values selected) → outline chip, gray
 *   - 1 value selected → filled chip with value inline ("Time: Opening Drive")
 *   - 2+ values → filled chip with count badge ("Time (3)")
 *   - All values selected → treated as Off (no filter is being applied)
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';

const SlicerChipDropdown = ({
  label,                    // chip label e.g. "Time"
  values = [],              // currently-selected value IDs (committed)
  options = [],             // [{ id, label, count? }] — count optional (renders "· N")
  onApply,                  // (newValues) => void
  onClear,                  // () => void
  totalKnownValues = null,  // total possible values in this dim (for "all selected = off" check)
  darkMode = true,
  warning = null,           // optional string — shown at top of dropdown (e.g. "Needs backfill")
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(values);
  const wrapRef = useRef(null);

  // Keep draft in sync when committed values change externally (e.g., Clear All)
  useEffect(() => {
    if (!open) setDraft(values);
  }, [values, open]);

  // When the dropdown opens, snapshot current values into draft
  useEffect(() => {
    if (open) setDraft(values);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click-outside-to-cancel
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setDraft(values); // discard draft
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open, values]);

  // Determine chip visual state
  const selectedCount = values.length;
  const totalAvailable = totalKnownValues ?? options.length;
  // "All selected within this dim" = treated as off (no filter applied)
  const isOff = selectedCount === 0 || (totalAvailable > 0 && selectedCount === totalAvailable);

  const chipLabel = (() => {
    if (isOff) return label;
    if (selectedCount === 1) {
      const opt = options.find(o => o.id === values[0]);
      return `${label}: ${opt?.label || values[0]}`;
    }
    return `${label} (${selectedCount})`;
  })();

  // Sum the total trade count under the current draft
  const draftTotal = useMemo(() => {
    if (draft.length === 0) return options.reduce((s, o) => s + (o.count || 0), 0);
    return draft.reduce((s, id) => {
      const o = options.find(x => x.id === id);
      return s + (o?.count || 0);
    }, 0);
  }, [draft, options]);

  const toggle = (id) => {
    setDraft(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  };

  const handleApply = () => {
    onApply?.(draft);
    setOpen(false);
  };

  const handleResetInDropdown = () => {
    setDraft([]);
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    onClear?.();
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
          isOff
            ? (darkMode
                ? 'bg-transparent border-gray-700 text-gray-300 hover:border-gray-500 hover:text-gray-100'
                : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400 hover:text-gray-900')
            : (darkMode
                ? 'bg-purple-500/20 border-purple-400/50 text-purple-100 ring-1 ring-purple-400/30'
                : 'bg-purple-100 border-purple-400 text-purple-800 ring-1 ring-purple-300/50')
        }`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <span className="truncate max-w-[160px]">{chipLabel}</span>
        {!isOff && (
          // Nested <button> inside <button> is invalid HTML (React warns
          // with validateDOMNesting). Use a span with role="button" +
          // keyboard handler — same accessibility, valid markup.
          <span
            role="button"
            tabIndex={0}
            onClick={handleClearAll}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleClearAll(e);
              }
            }}
            className={`ml-0.5 -mr-0.5 p-px rounded hover:bg-white/10 inline-flex items-center cursor-pointer`}
            aria-label={`Clear ${label} filter`}
            title="Clear this filter"
          >
            <X size={10} />
          </span>
        )}
        <ChevronDown size={10} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className={`absolute z-50 mt-1 w-64 rounded-lg shadow-xl border backdrop-blur-md ${
            darkMode
              ? 'bg-gray-900/95 border-gray-700 text-gray-100'
              : 'bg-white border-gray-300 text-gray-800'
          }`}
          role="listbox"
        >
          <div className={`px-3 py-2 border-b ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
            <p className={`text-[10px] uppercase tracking-wider font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {label}
            </p>
          </div>

          {warning && (
            <div className={`px-3 py-2 text-[10px] leading-snug border-b ${
              darkMode ? 'border-gray-800 bg-amber-500/10 text-amber-200' : 'border-gray-200 bg-amber-50 text-amber-800'
            }`}>
              ⚠ {warning}
            </div>
          )}

          <div className="max-h-72 overflow-y-auto py-1">
            {options.length === 0 ? (
              <p className={`px-3 py-2 text-[11px] italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                No values available
              </p>
            ) : options.map(opt => {
              const isSel = draft.includes(opt.id);
              const isDisabled = (opt.count ?? null) === 0 && !isSel;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => !isDisabled && toggle(opt.id)}
                  disabled={isDisabled}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[11px] text-left transition-colors ${
                    isDisabled
                      ? 'opacity-40 cursor-not-allowed'
                      : (darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50')
                  }`}
                  role="option"
                  aria-selected={isSel}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded border ${
                      isSel
                        ? (darkMode ? 'bg-purple-500 border-purple-400' : 'bg-purple-500 border-purple-600')
                        : (darkMode ? 'bg-transparent border-gray-600' : 'bg-white border-gray-400')
                    }`}>
                      {isSel && <Check size={9} className="text-white" strokeWidth={3} />}
                    </span>
                    <span className="truncate">{opt.label}</span>
                  </span>
                  {opt.count != null && (
                    <span className={`tabular-nums text-[10px] ${
                      isDisabled
                        ? (darkMode ? 'text-gray-700' : 'text-gray-300')
                        : (darkMode ? 'text-gray-500' : 'text-gray-500')
                    }`}>
                      · {opt.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className={`flex items-center gap-2 px-3 py-2 border-t ${darkMode ? 'border-gray-800 bg-black/20' : 'border-gray-200 bg-gray-50'}`}>
            <button
              type="button"
              onClick={handleResetInDropdown}
              className={`text-[10px] px-2 py-1 rounded transition-colors ${
                darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Reset
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={handleApply}
              className={`text-[10px] font-semibold px-2.5 py-1 rounded transition-colors ${
                darkMode
                  ? 'bg-purple-500/30 text-purple-100 hover:bg-purple-500/40 border border-purple-400/40'
                  : 'bg-purple-500 text-white hover:bg-purple-600'
              }`}
            >
              Apply
              <span className="ml-1 opacity-80 tabular-nums">({draftTotal})</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SlicerChipDropdown;
